import "../../styles/PagesStyles/TermsAndCondition.css"
import usePageTitle from "../../hooks/usePageTitle";

const TermsAndCondition = () => {
  usePageTitle("Combat Solutions AI | Terms and Conditions");
  return (
    <section className="terms-section">
      <div className="terms-container">

        {/* ===== Page Title Section ===== */}
        <div className="terms-header">
          <h1>Terms of Service</h1>
          <p className="terms-subtitle">
            These Terms define the rights, responsibilities, and obligations
            when accessing Combat Solutions AI services.
          </p>
          <div className="terms-divider"></div>
        </div>

        {/* ===== Scope of Professional Services ===== */}
        <h2>Scope of Professional Services</h2>
        <p>
          Combat Solutions is a technology-driven firm specializing in complex
          digital architectures. The Services provided include, but are not
          limited to:
        </p>

        <ul>
          <li>
            <strong>Intelligence & Machine Learning Systems:</strong> The
            development, implementation, and maintenance of advanced automated
            intelligence models and neural frameworks.
          </li>
          <li>
            <strong>Data Architecture & Migration:</strong> The transformation,
            integration, and visualization of enterprise-level data structures
            across various industrial formats.
          </li>
          <li>
            <strong>Technical Consulting & Strategy:</strong> Strategic advisory
            services focused on software lifecycle management, system
            integration, and digital infrastructure optimization.
          </li>
          <li>
            <strong>Custom Software Development:</strong> The design and
            deployment of modern application frameworks tailored to specific
            organizational requirements. The specific technical requirements
            and deliverables for any engagement shall be governed by a separate
            Statement of Work (SOW) or Project Agreement.
          </li>
        </ul>

        {/* ===== Intellectual Property ===== */}
        <h2>Intellectual Property and Proprietary Rights</h2>

        <h3>Company Property</h3>
        <p>
          All software, source code, specialized algorithms, proprietary
          methodologies, technical logic, and documentation developed by Combat
          Solutions in the course of providing the Services are and shall remain
          the sole and exclusive property of the Company.
        </p>

        <h3>Client Materials</h3>
        <p>
          The Client retains all rights and ownership in any data, materials, or
          information provided to the Company for the purpose of executing the
          Services (“Client Materials”). Combat Solutions is granted a limited
          license to use these materials solely for the duration of the project
          to fulfill its technical obligations.
        </p>

        <h3>Licensed Deliverables</h3>
        <p>
          Upon the successful conclusion of a project, Combat Solutions grants
          the Client a non-exclusive, non-transferable license to utilize the
          specific project deliverables for internal business operations. The
          Client is expressly prohibited from selling, sublicensing, or
          reverse-engineering any proprietary AI logic or source code provided
          by the Company.
        </p>

        {/* ===== Confidentiality ===== */}
        <h2>Confidentiality and Mutual Non-Disclosure</h2>
        <p>
          Both parties acknowledge that in the course of their professional
          relationship, they may have access to “Confidential Information”.
        </p>

        <ul>
          <li>
            <strong>Obligations:</strong> Each party agrees to protect the
            other’s Confidential Information with a high degree of care and to
            use such information only for the purpose of performing the
            Services.
          </li>
          <li>
            <strong>Scope:</strong> Confidential Information includes, but is not
            limited to, proprietary technical data, business strategies, trade
            secrets, and non-public information regarding system architectures
            or automated models.
          </li>
          <li>
            <strong>Duration:</strong> These confidentiality obligations shall
            survive the termination of any Service agreement for a period of
            three (3) years.
          </li>
        </ul>

        {/* ===== Data Security ===== */}
        <h2>Data Security and Privacy</h2>
        <p>
          Combat Solutions is committed to the highest standards of data
          integrity and security.
        </p>

        <ul>
          <li>
            <strong>Safeguards:</strong> We implement industry-standard
            administrative, physical, and technical controls (such as
            multi-layered encryption and restricted access protocols) to
            safeguard all data handled during the Services.
          </li>
          <li>
            <strong>Compliance:</strong> Our data handling practices are governed
            by the relevant information technology and data protection laws of
            India.
          </li>
          <li>
            <strong>Risk Acknowledgment:</strong> While the Company employs
            rigorous security measures, the Client acknowledges that the
            transmission of data over digital networks inherently involves risks
            beyond the Company’s absolute control.
          </li>
        </ul>

        {/* ===== Warranties ===== */}
        <h2>Representations and Warranties</h2>

        <h3>Professional Standard</h3>
        <p>
          Combat Solutions warrants that the Services will be performed with
          professional diligence and in accordance with generally recognized
          industry standards for IT and AI services.
        </p>

        <h3>Technical Disclaimer</h3>
        <p>
          ALL SERVICES ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS.
          EXCEPT AS EXPRESSLY STATED, COMBAT SOLUTIONS DISCLAIMS ALL WARRANTIES,
          INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A
          PARTICULAR PURPOSE.
        </p>

        {/* ===== Limitation of Liability ===== */}
        <h2>Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMBAT SOLUTIONS SHALL NOT BE
          LIABLE FOR ANY INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL
          DAMAGES.
        </p>

        {/* ===== Indemnification ===== */}
        <h2>Indemnification</h2>
        <p>
          The Client agrees to indemnify, defend, and hold harmless Combat
          Solutions and its employees, officers, and agents from any third-party
          claims arising from:
        </p>

        <ol>
          <li>The Client’s unauthorized use of the provided technology.</li>
          <li>A breach of these Terms by the Client.</li>
          <li>
            Any infringement of intellectual property rights contained within
            the Client Materials provided to the Company.
          </li>
        </ol>

        {/* ===== Termination ===== */}
        <h2>Terms and Termination</h2>

        <h3>Duration</h3>
        <p>
          These Terms shall remain in full force and effect for the duration of
          any active project or Statement of Work.
        </p>

        <h3>Termination for Cause</h3>
        <p>
          Either party may terminate the professional relationship if the other
          party materially breaches these Terms and fails to rectify such breach
          within 30 days of receiving written notice.
        </p>

        <h3>Effect of Termination</h3>
        <p>
          Upon termination, the Client shall return or destroy all proprietary
          Company materials in their possession.
        </p>

        {/* ===== General Provisions ===== */}
        <h2>General Provisions</h2>

        <ul>
          <li>
            <strong>Relationship of Parties:</strong> The parties are independent
            contractors.
          </li>
          <li>
            <strong>Severability:</strong> Remaining provisions continue in full
            force if one is invalid.
          </li>
          <li>
            <strong>Force Majeure:</strong> No liability for events beyond
            reasonable control.
          </li>
          <li>
            <strong>Governing Law:</strong> Laws of India apply.
          </li>
          <li>
            <strong>Exclusive Jurisdiction:</strong> Courts of Nashik,
            Maharashtra, India.
          </li>
        </ul>

        {/* ===== Contact ===== */}
        <h2>Contact</h2>
        <p>
          <strong>Combat Solutions AI</strong><br />
          Email: <a href="mailto:sales@combatsolutions.co.in">sales@combatsolutions.co.in</a>
        </p>

      </div>
    </section>
  );
};

export default TermsAndCondition;
